/*** Copyright 2026 Parts Pioneer Inc. ***/
let ymm_utils = {
    get_ymm_params: function () {
        try {
            let raw = localStorage.getItem("ymm");
            if (!raw) return null;
            let parsed = JSON.parse(raw);
            if (!parsed?.year || !parsed?.make || !parsed?.model) return null;
            return {
                year: parsed.year,
                make: parsed.make,
                model: parsed.model,
            };
        } catch (e) {
            return null;
        }
    },

    set_ymm_params: function (year, make, model) {
        let valid = year && make && model;
        let data = valid ? { year, make, model } : null;
        if (data) localStorage.setItem("ymm", JSON.stringify(data));
        else localStorage.removeItem("ymm");
        window.selectedYmm = data;
    },
};

function clearAndDisable(select, hide = false) {
    if (!select) return;

    const placeholder = select.dataset.placeholder;
    select.querySelectorAll("option").forEach((opt) => {
        if (opt.textContent.trim() !== placeholder) {
            opt.remove();
        }
    });

    select.disabled = true;
    if (hide) select.classList.add("hidden");
}

function addOptions(select, options) {
    if (!select || !Array.isArray(options)) return;

    options.forEach((item) => {
        const option = document.createElement("option");
        if (item && typeof item === "object") {
            option.value = item.value ?? "";
            option.textContent = item.text ?? item.label ?? item.value ?? "";
        } else {
            option.value = item;
            option.textContent = item;
        }
        select.append(option);
    });
}

function getYmmHandle(val) {
    return val.replace(/ /g, "-").toLowerCase();
}

function normalizeHandle(val) {
    let output = val.toLowerCase();
    output = output.replace(/[^a-z0-9]+/g, "-");
    output = output.replace(/-$/, "").replace(/^-/, "");
    return output;
}

window.selectedYmm = ymm_utils.get_ymm_params();

class YMM {
    constructor(id_prefix, search_query = "") {
        this.api = "https://x3g856x3w4.execute-api.us-east-1.amazonaws.com/v1/ymm";
        this.store = "partspioneer";

        this.search_query = search_query;
        this.in_stock_only = true;
        this.sort_param = "";
        this.isInitializing = true;
        this.showCategory = false;
        this.showProduct = false;

        // elements
        this.yearSelect = document.querySelector(`#${id_prefix}-ymm-searchbox-year`);
        this.makeSelect = document.querySelector(`#${id_prefix}-ymm-searchbox-make`);
        this.modelSelect = document.querySelector(`#${id_prefix}-ymm-searchbox-model`);
        this.categorySelect = document.querySelector(`#${id_prefix}-ymm-searchbox-category`);
        this.productSelect = document.querySelector(`#${id_prefix}-ymm-searchbox-product`);
        this.selectButton = document.querySelector(`#${id_prefix}-ymm-searchbox-select`);
        this.resetButton = document.querySelector(`#${id_prefix}-ymm-searchbox-reset`);

        if (!this.showCategory && this.categorySelect) {
            this.categorySelect.classList.add("hidden");
            this.categorySelect.disabled = true;
        }

        if (!this.showProduct && this.productSelect) {
            this.productSelect.classList.add("hidden");
            this.productSelect.disabled = true;
        }

        this.ymm = ymm_utils.get_ymm_params();
        this.data = null;

        this.bind();
        this.load();
    }

    /* ---------------- INIT ---------------- */

    bind() {
        if (this.yearSelect) this.yearSelect.addEventListener("change", () => this.onYear());
        if (this.makeSelect) this.makeSelect.addEventListener("change", () => this.onMake());
        if (this.modelSelect) this.modelSelect.addEventListener("change", () => this.onModel());
        if (this.categorySelect) this.categorySelect.addEventListener("change", () => this.onCategory());
        if (this.productSelect) this.productSelect.addEventListener("change", () => this.submit());
        if (this.selectButton) this.selectButton.addEventListener("click", () => this.submit());
        if (this.resetButton) this.resetButton.addEventListener("click", () => this.reset());
    }

    load() {
        let cached = sessionStorage.getItem("ymm_data");

        if (cached) {
            try {
                let p = JSON.parse(cached);
                if (Date.now() - p.time < 60 * 60 * 1000) {
                    this.data = p.data;
                    this.initYears();
                    this.prefill();
                    return;
                }
            } catch {}
        }

        fetch(`${this.api}/all?store=${encodeURIComponent(this.store)}`)
            .then((res) => {
                if (!res.ok) throw new Error("Failed to fetch YMM data");
                return res.json();
            })
            .then((res) => {
                this.data = res.vehicles;
                sessionStorage.setItem(
                    "ymm_data",
                    JSON.stringify({
                        data: this.data,
                        time: Date.now(),
                    }),
                );
                this.initYears();
                this.prefill();
            })
            .catch(() => {
                // ignore fetch failure
            });
    }

    initYears() {
        clearAndDisable(this.yearSelect);
        addOptions(this.yearSelect, Object.keys(this.data).sort((a, b) => b - a));
        if (this.yearSelect) this.yearSelect.disabled = false;
    }

    prefill() {
        if (!this.ymm) {
            this.isInitializing = false;
            return;
        }

        let { year, make, model } = this.ymm;
        if (!year || !make || !model) {
            this.isInitializing = false;
            return;
        }

        if (!this.data[year]) {
            this.isInitializing = false;
            ymm_utils.set_ymm_params(null, null, null);
            return;
        }

        if (this.yearSelect) {
            this.yearSelect.value = year;
            this.onYear();
        }

        if (!this.data[year]?.[make]) {
            this.isInitializing = false;
            ymm_utils.set_ymm_params(null, null, null);
            return;
        }

        if (this.makeSelect) {
            this.makeSelect.value = make;
            this.onMake();
        }

        if (!this.data[year]?.[make]?.includes(model)) {
            this.isInitializing = false;
            ymm_utils.set_ymm_params(null, null, null);
            return;
        }

        if (this.modelSelect) {
            this.modelSelect.value = model;
            this.onModel();
        }

        this.isInitializing = false;
    }
    /* ---------------- FLOW ---------------- */

    onYear() {
        const year = this.yearSelect?.value || "";

        clearAndDisable(this.makeSelect);
        clearAndDisable(this.modelSelect);
        if (this.showCategory) clearAndDisable(this.categorySelect, true);
        if (this.showProduct) clearAndDisable(this.productSelect, true);

        this.disable();

        if (!year) return;

        addOptions(this.makeSelect, Object.keys(this.data[year] || {}).sort());
        if (this.makeSelect) this.makeSelect.disabled = false;
    }

    onMake() {
        const year = this.yearSelect?.value || "";
        const make = this.makeSelect?.value || "";

        clearAndDisable(this.modelSelect);
        if (this.showCategory) clearAndDisable(this.categorySelect, true);
        if (this.showProduct) clearAndDisable(this.productSelect, true);

        this.disable();

        if (!year || !make) return;

        addOptions(this.modelSelect, (this.data[year]?.[make] || []).slice().sort());
        if (this.modelSelect) this.modelSelect.disabled = false;
    }

    onModel() {
        const year = this.yearSelect?.value || "";
        const make = this.makeSelect?.value || "";
        const model = this.modelSelect?.value || "";
        if (this.showCategory) clearAndDisable(this.categorySelect, true);
        if (this.showProduct) clearAndDisable(this.productSelect, true);

        if (!model) {
            this.disable();
            return;
        }

        this.enable();

        if (this.isProductPage()) {
            if (this.isInitializing) return;
            return this.submit();
        }

        if (this.showCategory) {
            const handle = getYmmHandle(`${year}-${make}-${model}`);

            this.getFilters(handle).then((f) => {
                const categories = f.find((x) => x.param_name === "filter.p.m.aces.category")?.values || [];

                clearAndDisable(this.categorySelect);

                categories.forEach((c) => {
                    if (!this.categorySelect) return;
                    const option = document.createElement("option");
                    option.value = c.value;
                    option.textContent = c.label;
                    this.categorySelect.append(option);
                });

                if (this.categorySelect) {
                    this.categorySelect.disabled = false;
                    this.categorySelect.classList.remove("hidden");
                }
            });
        } else if (!this.isInitializing) {
            return this.submit();
        }
    }

    onCategory() {
        if (this.showProduct) clearAndDisable(this.productSelect, true);

        const category = this.categorySelect?.value || "";
        if (!category) return;

        const year = this.yearSelect?.value || "";
        const make = this.makeSelect?.value || "";
        const model = this.modelSelect?.value || "";

        if (!this.showProduct && !this.isInitializing) return this.submit();

        const handle = getYmmHandle(`${year}-${make}-${model}`);

        this.getFilters(handle, category).then((f) => {
            const products = f.find((x) => x.param_name === "filter.p.product_type")?.values.map((v) => v.value || v.label) || [];

            if (products.length === 1 && !this.isInitializing) {
                if (this.productSelect) this.productSelect.value = products[0];
                return this.submit();
            }

            clearAndDisable(this.productSelect);
            addOptions(this.productSelect, products);
            if (this.productSelect) {
                this.productSelect.disabled = false;
                this.productSelect.classList.remove("hidden");
            }
        });
    }

    /* ---------------- ACTIONS ---------------- */

    submit() {
        const y = this.yearSelect?.value || "";
        const m = this.makeSelect?.value || "";
        const mo = this.modelSelect?.value || "";
        if (!y || !m || !mo) return;

        if (this.selectButton) {
            this.selectButton.classList.add("loading");
            this.selectButton.querySelector(".loading__spinner")?.classList.remove("hidden");
        }

        ymm_utils.set_ymm_params(y, m, mo);

        const handle = getYmmHandle(`${y}-${m}-${mo}`);
        if (normalizeHandle(handle) !== handle) return;

        if (this.isProductPage()) {
            location.reload();
            return;
        }

        let url = "/collections/" + handle;
        const q = [];

        if (this.in_stock_only) q.push("filter.v.availability=1");
        if (this.sort_param) q.push(this.sort_param);
        if (this.search_query) q.push(this.search_query);

        const cat = this.showCategory ? this.categorySelect?.value : null;
        const prod = this.showProduct ? this.productSelect?.value : null;

        if (cat && !prod) q.push("filter.p.m.aces.category=" + encodeURIComponent(cat));
        if (prod) q.push("filter.p.product_type=" + encodeURIComponent(prod));

        if (q.length) url += "?" + q.join("&");

        location.href = url;
    }

    reset() {
        if (this.yearSelect) this.yearSelect.value = "";
        clearAndDisable(this.makeSelect);
        clearAndDisable(this.modelSelect);
        clearAndDisable(this.categorySelect, true);
        clearAndDisable(this.productSelect, true);

        this.disable();
        ymm_utils.set_ymm_params(null, null, null);
    }

    /* ---------------- UTIL ---------------- */

    getFilters(handle, category = null) {
        let url = `/collections/${handle}?view=filters`;
        if (category) url += `&filter.p.m.aces.category=${category}`;
        return fetch(url)
            .then((r) => r.json())
            .then((d) => d.filters || []);
    }

    enable() {
        if (this.selectButton) this.selectButton.disabled = false;
    }

    disable() {
        if (this.selectButton) this.selectButton.disabled = true;
    }

    isProductPage() {
        return location.pathname.startsWith("/products/");
    }
}
